import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import styled from 'styled-components';
import { currencySymbolMap } from '../../../utils/currencies';
import {
  RevenueSkeletonTitle,
  RevenueSkeletonLegend,
  RevenueSkeletonLegendItem,
  RevenueSkeletonChart,
  RevenueSkeletonChartGrid,
  RevenueSkeletonChartGridLine,
  RevenueSkeletonChartLine,
} from '../../DashBoardNew/dashBoardNewStyles';


const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
  width: 100%;
  height: 100%;
  min-height: 260px;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 8px;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 1024px) {
    min-height: 240px;
  }

  @media (max-width: 768px) {
    min-height: 280px;
    padding: 12px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    min-height: 250px;
    padding: 10px;
    gap: 8px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TitleText = styled.h3`
  margin: 0;
  font-weight: 700;
  font-size: 14px;
  color: #06100f;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  flex: 0 0 auto;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 3px;
  }

  @media (max-width: 480px) {
    flex: 1 1 auto;
  }
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 8px;
  gap: 4px;
  width: 57px;
  height: 20px;
  background: ${({ color }) => color};
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  color: ${({ textColor }) => textColor};

  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 11px;
    width: auto;
    height: auto;
  }

  @media (max-width: 480px) {
    padding: 6px 4px;
    font-size: 10px;
  }
`;

const LegendDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${({ color }) => color};
  border-radius: 50%;
`;

const LegendText = styled.span`
  font-weight: 600;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 196px;
  position: relative;
  flex: 1;

  @media (max-width: 1024px) {
    height: 180px;
  }

  @media (max-width: 768px) {
    height: 160px;
  }

  @media (max-width: 480px) {
    height: 140px;
  }
`;

const RevenueCompareChart = ({
  getDashboardRevenue,
  revenueIsLoading,
  selectedOrganization,
  lastYearRevenue,
  thisYearRevenue,
  currentMonthIndex: propCurrentMonthIndex,
}) => {
  const chartRef = useRef(null);
  const currency = localStorage.getItem('currency') || 'USD';
  const currencySymbol = currencySymbolMap[currency] || '$';
  const [revenueData, setRevenueData] = useState({
    lastYear: null,
    thisYear: null,
    currentMonthIndex: null,
  });

  // Call API when component mounts or organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardRevenue) {
      const payload = {
        organization_id: selectedOrganization.id,
      };
      getDashboardRevenue(payload);
    }
  }, [selectedOrganization, getDashboardRevenue]);

  // Update local state when props change (from Redux)
  useEffect(() => {
    if (lastYearRevenue || thisYearRevenue) {
      setRevenueData({
        lastYear: lastYearRevenue,
        thisYear: thisYearRevenue,
        currentMonthIndex: propCurrentMonthIndex !== null && propCurrentMonthIndex !== undefined
          ? propCurrentMonthIndex
          : null,
      });
    }
  }, [lastYearRevenue, thisYearRevenue, propCurrentMonthIndex]);

  const currentMonthIndex = revenueData.currentMonthIndex !== null && revenueData.currentMonthIndex !== undefined
    ? revenueData.currentMonthIndex
    : new Date().getMonth(); // Fallback to current month if not provided

  // Plugin to create and apply gradients
  const gradientPlugin = {
    id: 'gradientPlugin',
    afterDatasetsDraw: (chart) => {
      const { ctx, chartArea } = chart;
      const yScale = chart.scales.y;

      if (!yScale) return;

      // Create Purple Gradient (2026)
      const purpleGradient = ctx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom
      );
      purpleGradient.addColorStop(0.0426, 'rgba(244, 236, 254, 0.95)');
      purpleGradient.addColorStop(0.9679, 'rgba(244, 236, 254, 0)');

      // Create Blue Gradient (2025)
      const blueGradient = ctx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom
      );
      blueGradient.addColorStop(0.3898, 'rgba(34, 102, 239, 0.12)');
      blueGradient.addColorStop(1, 'rgba(34, 102, 239, 0)');

      // Apply gradients to datasets
      chart.data.datasets[0].backgroundColor = purpleGradient;
      chart.data.datasets[1].backgroundColor = blueGradient;
    },
  };

  // Prepare chart data from API response
  const thisYearData = revenueData.thisYear?.data || [];
  const lastYearData = revenueData.lastYear?.data || [];
  const thisYearLabel = revenueData.thisYear?.year || new Date().getFullYear();
  const lastYearLabel = revenueData.lastYear?.year || new Date().getFullYear() - 1;

  // Ensure we have 12 months of data (pad with 0 if needed)
  const padArray = (arr, length) => {
    const padded = [...arr];
    while (padded.length < length) {
      padded.push(0);
    }
    return padded.slice(0, length);
  };

  const paddedThisYearData = padArray(thisYearData, 12);
  const paddedLastYearData = padArray(lastYearData, 12);

  const chartData = {
    labels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    datasets: [
      {
        label: String(thisYearLabel),
        data: paddedThisYearData,
        borderColor: '#6C2EF2',
        borderWidth: 2,
        pointRadius: ctx => {
          return ctx.dataIndex === currentMonthIndex ? 1 : 4;
        },
        pointBackgroundColor: '#6C2EF2',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointHitRadius: ctx => {
          return ctx.dataIndex === currentMonthIndex ? 15 : 5;
        },
        tension: 0.4,
        fill: true,
      },
      {
        label: String(lastYearLabel),
        data: paddedLastYearData,
        borderColor: '#2266EF',
        borderWidth: 2,
        pointRadius: ctx => {
          return ctx.dataIndex === currentMonthIndex ? 1 : 4;
        },
        pointBackgroundColor: '#2266EF',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointHitRadius: ctx => {
          return ctx.dataIndex === currentMonthIndex ? 15 : 5;
        },
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: 'transparent',
    interaction: {
      intersect: true,
      mode: 'nearest',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#FFFFFF',
        titleColor: '#06100F',
        bodyColor: '#06100F',
        borderColor: '#EFF4F3',
        borderWidth: 1,
        padding: 4,
        borderRadius: 8,
        bodyFont: {
          size: 12,
          weight: 600,
        },
        boxShadow: '0px 2px 20px rgba(28, 79, 74, 0.15)',
        displayColors: false,
        yAlign: 'bottom',
        xAlign: 'center',
        callbacks: {
          title: function () {
            return '';
          },
          label: function (context) {
            return currencySymbol + context.parsed.y + 'K';
          },
          labelTextColor: function (context) {
            return context.dataset.borderColor;
          },
          afterLabel: function () {
            return '';
          },
        },
      },
      // Plugin for drawing vertical line at current month
      verticalLine: {
        id: 'verticalLine',
        afterDraw: chart => {
          const { ctx, chartArea } = chart;
          const xScale = chart.scales.x;
          const yScale = chart.scales.y;

          if (!xScale || !yScale) return;

          // Get the pixel position of the current month
          const currentMonthPixelX = xScale.getPixelForValue(currentMonthIndex);

          ctx.save();
          ctx.strokeStyle = '#6C2EF2';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 4]); // Dashed line
          ctx.beginPath();
          ctx.moveTo(currentMonthPixelX, chartArea.top);
          ctx.lineTo(currentMonthPixelX, chartArea.bottom);
          ctx.stroke();
          ctx.restore();
        },
      },
      // Plugin for drawing custom pointer icons
      pointerIcons: {
        id: 'pointerIcons',
        afterDraw: chart => {
          const { ctx } = chart;
          const xScale = chart.scales.x;
          const yScale = chart.scales.y;

          if (!xScale || !yScale) return;

          chart.data.datasets.forEach((dataset, datasetIndex) => {
            const data = dataset.data;
            const color = dataset.borderColor;

            if (datasetIndex === 0 || datasetIndex === 1) {
              const currentValue = data[currentMonthIndex];
              const xPixel = xScale.getPixelForValue(currentMonthIndex);
              const yPixel = yScale.getPixelForValue(currentValue);

              ctx.save();

              // Draw outer circle with 4px radius and border color (stroke)
              ctx.strokeStyle = color;
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              ctx.arc(xPixel, yPixel, 6, 0, 2 * Math.PI);
              ctx.stroke();

              // Draw inner circle with 2px radius and fill color
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(xPixel, yPixel, 3, 0, 2 * Math.PI);
              ctx.fill();

              ctx.restore();
            }
          });
        },
      },

    },
    scales: {
      x: {
        grid: {
          display: true,
          color: '#DBE6E480',
          drawBorder: false,
        },
        ticks: {
          color: '#3C4D4B',
          font: {
            size: 12,
            weight: 400,
            family: 'Mona Sans',
          },
          padding: 0,
        },
        stacked: false,
      },
      y: {
        beginAtZero: true,
        max: 400,
        grid: {
          display: true,
          color: '#DBE6E480',
          drawBorder: false,
        },
        ticks: {
          color: '#7B8281',
          font: {
            size: 12,
            weight: 400,
            family: 'Mona Sans',
          },
          callback: function (value) {
            return currencySymbol + value + 'K';
          },
          padding: 0,
        },
        stacked: false,
      },
    },
  };

  // Calculate max value for Y axis
  const maxValue = Math.max(
    ...paddedThisYearData,
    ...paddedLastYearData,
    400 // Minimum max value
  );
  const yAxisMax = Math.ceil(maxValue / 100) * 100; // Round up to nearest 100

  // Update chart options with dynamic max
  const updatedChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        max: yAxisMax,
      },
    },
  };

  if (revenueIsLoading) {
    return (
      <CardContainer>
        <HeaderContainer>
          <RevenueSkeletonTitle />
          <RevenueSkeletonLegend>
            <RevenueSkeletonLegendItem />
            <RevenueSkeletonLegendItem />
          </RevenueSkeletonLegend>
        </HeaderContainer>
        <RevenueSkeletonChart>
          <RevenueSkeletonChartGrid>
            {/* Grid lines */}
            {[0, 1, 2, 3, 4].map((index) => (
              <RevenueSkeletonChartGridLine key={`grid-${index}`} />
            ))}
            {/* Simulate two line chart paths with wavy appearance */}
            <RevenueSkeletonChartLine top="25%" left="5%" width="88%" />
            <RevenueSkeletonChartLine top="30%" left="8%" width="85%" transform="rotate(-2deg)" />
            <RevenueSkeletonChartLine top="35%" left="10%" width="90%" />

            <RevenueSkeletonChartLine top="60%" left="5%" width="92%" />
            <RevenueSkeletonChartLine top="65%" left="8%" width="88%" transform="rotate(1deg)" />
            <RevenueSkeletonChartLine top="70%" left="10%" width="90%" />
          </RevenueSkeletonChartGrid>
        </RevenueSkeletonChart>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <HeaderContainer>
        <TitleText>Revenue {thisYearLabel} vs {lastYearLabel}</TitleText>
        <LegendContainer>
          <LegendItem color="#F4ECFE" textColor="#6C2EF2">
            <LegendDot color="#6C2EF2" />
            <LegendText>{thisYearLabel}</LegendText>
          </LegendItem>
          <LegendItem color="#EDF8FE" textColor="#2266EF">
            <LegendDot color="#2266EF" />
            <LegendText>{lastYearLabel}</LegendText>
          </LegendItem>
        </LegendContainer>
      </HeaderContainer>
      <ChartContainer>
        <Line
          ref={chartRef}
          data={chartData}
          options={updatedChartOptions}
          plugins={[
            gradientPlugin,
            updatedChartOptions.plugins.verticalLine,
            updatedChartOptions.plugins.pointerIcons,
          ]}
        />
      </ChartContainer>
    </CardContainer>
  );
};

export default RevenueCompareChart;